/**
 * Detecta eventos en la medicion continua: un corte, perdida, jitter o latencia
 * sostenidos. Sin timers ni red, para probarlo sin navegador.
 *
 * Un segundo malo suelto no es un evento: hace falta `minSeconds` seguidos, y
 * el evento termina cuando pasan `QUIET_SECONDS` buenos. Se usan los umbrales
 * "mal" de la pantalla, no los de "regular": con estos ultimos una conexion
 * normal generaria eventos todo el dia.
 */

import { BAD, Sample } from './summary';

export type EventKind = 'cut' | 'loss' | 'jitter' | 'latency';

export interface DetectedEvent {
    kind: EventKind;
    /** Cuanto duro el evento. */
    seconds: number;
    /** Valor maximo: % de perdida, o ms de jitter o de latencia. Nulo en un corte. */
    peak: number | null;
    /** Hace cuantos segundos empezo, contados hasta el momento en que se emite. */
    agoSeconds: number;
}

/** El limite de latencia de eventos es mas alto que el "mal" de la pantalla (300): ver arriba. */
const EVENT_RTT_MS = 400;
const QUIET_SECONDS = 3;

const RULES: Record<EventKind, { minSeconds: number }> = {
    cut: { minSeconds: BAD.cutSeconds },
    loss: { minSeconds: 2 },
    jitter: { minSeconds: 2 },
    latency: { minSeconds: 3 },
};

interface Streak {
    start: number;
    over: number;
    quiet: number;
    peak: number;
}

export default class EventDetector {
    private previous: Sample | null = null;

    private tick = 0;

    private lastRtt = 0;

    private audioStarted = false;

    private streaks: Partial<Record<EventKind, Streak>> = {};

    /** Despues de una pausa los contadores acumulados no se pueden comparar con los de antes. */
    reset(): DetectedEvent[] {
        const closed = this.flush();
        this.previous = null;

        return closed;
    }

    push(sample: Sample): DetectedEvent[] {
        const { previous } = this;
        this.previous = sample;
        this.tick += 1;

        if (!previous) {
            return [];
        }

        const received = Math.max(0, sample.received - previous.received);
        const lost = Math.max(0, sample.lost - previous.lost);
        const total = received + lost;
        this.lastRtt = sample.rttMs ?? this.lastRtt;
        this.audioStarted = this.audioStarted || received > 0;

        const values: Record<EventKind, { over: boolean; value: number }> = {
            cut: { over: this.audioStarted && received === 0, value: 0 },
            loss: {
                over: total > 0 && (lost / total) * 100 >= BAD.lossPct,
                value: total > 0 ? (lost / total) * 100 : 0,
            },
            jitter: { over: sample.jitterMs >= BAD.jitterMs, value: sample.jitterMs },
            latency: { over: this.lastRtt >= EVENT_RTT_MS, value: this.lastRtt },
        };

        return (Object.keys(values) as EventKind[])
            .map((kind) => this.advance(kind, values[kind].over, values[kind].value))
            .filter((event): event is DetectedEvent => event !== null);
    }

    /** Cierra lo que este abierto, para cuando la medicion termina. */
    flush(): DetectedEvent[] {
        const closed = (Object.keys(this.streaks) as EventKind[])
            .map((kind) => this.close(kind))
            .filter((event): event is DetectedEvent => event !== null);
        this.streaks = {};

        return closed;
    }

    private advance(kind: EventKind, over: boolean, value: number): DetectedEvent | null {
        const streak = this.streaks[kind];

        if (over) {
            if (!streak) {
                this.streaks[kind] = {
                    start: this.tick, over: 1, quiet: 0, peak: value,
                };
            } else {
                streak.over += 1;
                streak.quiet = 0;
                streak.peak = Math.max(streak.peak, value);
            }

            return null;
        }

        if (!streak) {
            return null;
        }

        streak.quiet += 1;

        return streak.quiet >= QUIET_SECONDS ? this.close(kind) : null;
    }

    private close(kind: EventKind): DetectedEvent | null {
        const streak = this.streaks[kind];
        delete this.streaks[kind];

        if (!streak || streak.over < RULES[kind].minSeconds) {
            return null;
        }

        return {
            kind,
            seconds: streak.over,
            peak: kind === 'cut' ? null : Math.round(streak.peak * 10) / 10,
            agoSeconds: this.tick - streak.start + 1,
        };
    }
}
